/**
 * Client-side validation mirroring the server's request models.
 *
 * The patterns below are copied verbatim from the authoritative sources — they
 * are NOT invented here:
 *  - `serial` / `new_serial`: `Field(..., pattern=r"^[A-Za-z0-9._-]{6,32}$")`
 *    (`ActivationRequest` / `ReplaceRequest` in `acs_service/models.py`)
 *  - `subscriber_ref`: `Field(None, pattern=r"^sub:")` (same file)
 *  - `target`: `DIAGNOSTICS_TARGET_PATTERN = r"^[A-Za-z0-9._:-]+$"` plus the
 *    `_target_is_not_an_option` validator (`DiagnosticsRequest`)
 *  - `olt_id` / `pon_port` / `line_profile` / `service_profile`: `min_length=1`
 *  - `vlan`: `int | None` (no range is enforced in v1, so none is asserted here)
 *
 * Why mirror it at all: a pattern violation is answered by FastAPI's *default*
 * 422 body-validation JSON, not an RFC 9457 problem (`models.py` docstring of
 * `DiagnosticsRequest` says so explicitly), so the console would have nothing
 * stable to render. Catching it here gives the operator a Persian message before
 * a request leaves the browser; the server stays the authority either way — a
 * rejected request is still rendered through `ProblemPanel`.
 *
 * Messages are Persian because this is the operator-facing surface; the wire
 * pattern is quoted inside them so a ticket can name the rule.
 */

/** `^[A-Za-z0-9._-]{6,32}$` — ONU serial (`ActivationRequest.serial`). */
export const SERIAL_PATTERN = /^[A-Za-z0-9._-]{6,32}$/;

/** `^[A-Za-z0-9._:-]+$` — probe host (`models.DIAGNOSTICS_TARGET_PATTERN`). */
export const DIAGNOSTICS_TARGET_PATTERN = /^[A-Za-z0-9._:-]+$/;

/** `^sub:` — the BSS subscriber reference prefix. */
export const SUBSCRIBER_REF_PREFIX = 'sub:';

/**
 * The server's own reason (`DiagnosticsRequest._target_is_not_an_option`): the
 * target is forwarded to the CPE diagnostics object, so a leading `-` would be
 * read as an option. Same reason, in Persian.
 */
export const OPTION_SHAPED_TARGET_REASON =
  'target نباید با «-» شروع شود: این مقدار به شیء عیب‌یابی روی دستگاه پاس داده می‌شود و ' +
  'در آن‌جا به‌عنوان گزینه/آرگومان تفسیر می‌شود (argument injection).';

export interface ActivationFormValues {
  serial: string;
  olt_id: string;
  pon_port: string;
  line_profile: string;
  service_profile: string;
  /** `vlan` is a text input; empty means "not sent". */
  vlan: string;
  subscriber_ref: string;
}

export type ActivationField = keyof ActivationFormValues;

export type FieldErrors<T extends string> = Partial<Record<T, string>>;

export function validateSerial(serial: string): string | null {
  const value = serial.trim();
  if (value.length === 0) return 'سریال ONU الزامی است.';
  if (!SERIAL_PATTERN.test(value)) {
    return 'سریال باید ۶ تا ۳۲ نویسه از حروف لاتین، ارقام و . _ - باشد ( الگو: ^[A-Za-z0-9._-]{6,32}$ ).';
  }
  return null;
}

export function validateSubscriberRef(ref: string): string | null {
  const value = ref.trim();
  if (value.length === 0) return null; // optional
  if (!value.startsWith(SUBSCRIBER_REF_PREFIX)) {
    return 'subscriber_ref باید با پیشوند «sub:» شروع شود ( e.g. sub:12345 ).';
  }
  return null;
}

export function validateVlan(vlan: string): string | null {
  const value = vlan.trim();
  if (value.length === 0) return null; // optional
  if (!/^-?\d+$/.test(value)) return 'vlan باید یک عدد صحیح باشد (مثال: 35).';
  return null;
}

/** One required free-text field, mirroring `min_length=1`. */
function required(value: string, label: string, example: string): string | null {
  if (value.trim().length === 0) return `${label} الزامی است (مثال: ${example}).`;
  return null;
}

/** Validates the whole activation form; empty object means "safe to submit". */
export function validateActivationForm(values: ActivationFormValues): FieldErrors<ActivationField> {
  const errors: FieldErrors<ActivationField> = {};
  const checks: [ActivationField, string | null][] = [
    ['serial', validateSerial(values.serial)],
    ['olt_id', required(values.olt_id, 'شناسهٔ OLT (olt_id)', 'olt-tehran-01')],
    ['pon_port', required(values.pon_port, 'پورت PON (pon_port)', 'PON-0/1')],
    ['line_profile', required(values.line_profile, 'پروفایل خط (line_profile)', 'LP-XGP-30')],
    [
      'service_profile',
      required(values.service_profile, 'پروفایل سرویس (service_profile)', 'SP-INET-1G'),
    ],
    ['vlan', validateVlan(values.vlan)],
    ['subscriber_ref', validateSubscriberRef(values.subscriber_ref)],
  ];
  for (const [field, message] of checks) {
    if (message) errors[field] = message;
  }
  return errors;
}

export function validateDiagnosticsTarget(target: string): string | null {
  const value = target.trim();
  if (value.length === 0) return 'مقصد (target) الزامی است.';
  if (value.startsWith('-')) return OPTION_SHAPED_TARGET_REASON;
  if (!DIAGNOSTICS_TARGET_PATTERN.test(value)) {
    return (
      'target فقط می‌تواند hostname خالی، IPv4 یا IPv6 باشد: نویسه‌های مجاز حروف لاتین، ' +
      'ارقام و . _ : - ( الگو: ^[A-Za-z0-9._:-]+$ ).'
    );
  }
  return null;
}

/** True when a target looks like a CLI option — the exact injection seam the server blocks. */
export function isOptionShapedTarget(target: string): boolean {
  return target.trim().startsWith('-');
}

/** `vlan` arrives as text; only send it when the operator actually typed one. */
export function parseOptionalInt(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** True when a form has no errors and at least one field was touched. */
export function hasErrors(errors: FieldErrors<string>): boolean {
  return Object.values(errors).some((message) => typeof message === 'string');
}
